/** Build JSON body for POST/PUT pricing plan API from local form state. */

import {
  masterApiErrorMessage,
  translateAggregateBackendMessage,
} from '../../../main/utils/masterApiError';

function trimOrEmpty(v) {
  return v == null ? '' : String(v).trim();
}

function intOrDefault(v, def) {
  const n = parseInt(String(v).trim(), 10);
  return Number.isFinite(n) ? n : def;
}

export function emptyPricingPlanForm() {
  return {
    code: '',
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    show_in_menu: false,
    menu_label_ar: '',
    menu_label_en: '',
    menu_order: 0,
    active: true,
  };
}

export function buildPricingPlanPayload(form) {
  return {
    code: trimOrEmpty(form.code),
    name: trimOrEmpty(form.name),
    name_ar: trimOrEmpty(form.name_ar) || null,
    description: trimOrEmpty(form.description) || null,
    description_ar: trimOrEmpty(form.description_ar) || null,
    show_in_menu: Boolean(form.show_in_menu),
    menu_label_ar: trimOrEmpty(form.menu_label_ar) || null,
    menu_label_en: trimOrEmpty(form.menu_label_en) || null,
    menu_order: intOrDefault(form.menu_order, 0),
    active: Boolean(form.active),
  };
}

export function pricingPlanFromApi(row) {
  if (!row || typeof row !== 'object') return emptyPricingPlanForm();
  return {
    code: row.code ?? '',
    name: row.name ?? '',
    name_ar: row.name_ar ?? '',
    description: row.description ?? '',
    description_ar: row.description_ar ?? '',
    show_in_menu: Boolean(row.show_in_menu),
    menu_label_ar: row.menu_label_ar ?? '',
    menu_label_en: row.menu_label_en ?? '',
    menu_order: row.menu_order ?? 0,
    active: row.active !== false,
  };
}

const CODE_MAX_LEN = 120;
const NAME_MAX_LEN = 200;

export function validatePricingPlanClient(form, t, opts = {}) {
  const { isCreate = false } = opts;
  const errors = {};
  const code = trimOrEmpty(form.code);
  if (!code) {
    errors.code = t('pricingPlans.validationCodeRequired');
  } else if (code.length > CODE_MAX_LEN) {
    errors.code = t('pricingPlans.validationCodeMaxLength', { max: CODE_MAX_LEN });
  } else if (isCreate && !/^[a-z0-9_]+$/.test(code)) {
    errors.code = t('pricingPlans.validationCodeSlug');
  }

  const name = trimOrEmpty(form.name);
  if (!name) {
    errors.name = t('pricingPlans.validationNameRequired');
  } else if (name.length > NAME_MAX_LEN) {
    errors.name = t('pricingPlans.validationNameMaxLength', { max: NAME_MAX_LEN });
  }

  return errors;
}

const PLAN_API_TOPLEVEL_KEYS = new Set([
  'detail',
  'non_field_errors',
  'message_key',
  'message',
  'error',
]);

function joinDrfMessages(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val.trim();
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && typeof item.message === 'string') return item.message;
        return '';
      })
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

export function pricingPlanApiValidationFeedback(err, t) {
  const fieldErrors = {};
  let banner = '';

  const data = err && err.response && err.response.data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
      banner = translateAggregateBackendMessage(
        data.non_field_errors.map(String).join('; '),
        t
      );
    }

    Object.keys(data).forEach((key) => {
      if (PLAN_API_TOPLEVEL_KEYS.has(key)) return;
      const joined = joinDrfMessages(data[key]);
      if (joined) {
        fieldErrors[key] = translateAggregateBackendMessage(joined, t);
      }
    });
  }

  if (!banner && Object.keys(fieldErrors).length === 0) {
    banner = masterApiErrorMessage(err, t);
  }

  return { fieldErrors, saveError: banner };
}
